import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Tabs from "../../components/Tabs";
import DataTable from "../../components/DataTable";
import { createLookupApi } from "../../api/lookupApi";
import { getExamGradingPassFailSummary } from "../../api/examApi";
import "../Attendance/Attendance.css";

const examApi = createLookupApi("exams");
const academicYearApi = createLookupApi("academic-years");

const URL_TO_TAB = {
  "exam-report-section-wise-grading-summary": "grading",
  "exam-report-section-wise-pass-fail-summary": "passfail",
};

const TABS = [
  { key: "grading", label: "Grading Summary" },
  { key: "passfail", label: "Pass & Fail Summary" },
];

/**
 * Direct port of digicampusbd's `exam-report-section-wise-grading-summary`
 * and `exam-report-section-wise-pass-fail-summary` (`ExamReportService`'s
 * `findSectionWiseGradingSummary`/`findSectionWisePassFailSummary`) — unlike
 * the merit-position reports, these are scoped by exam *name* (one exam
 * across every class-configuration in the institute), not one class+exam's
 * `examConfigId`, so they need their own picker and their own backend call
 * (`GET /api/exam-results/grading-pass-fail-summary`) rather than reusing
 * `GET /api/exam-results`. Both old endpoints loop over the same per-class-
 * configuration data, so this app returns one combined row per class-
 * configuration (`ExamGradingPassFailSummaryResponse`) and just renders
 * different columns per tab, instead of two near-identical backend calls.
 */
export default function ExamGradingPassFailSummaryPage() {
  const { pageUrl } = useParams();
  const [tab, setTab] = useState(URL_TO_TAB[pageUrl] || "grading");

  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setTab(URL_TO_TAB[pageUrl] || "grading");
  }, [pageUrl]);

  useEffect(() => {
    Promise.all([examApi.list(), academicYearApi.list()]).then(([e, y]) => {
      setExams(e);
      setExamId(e.length > 0 ? String(e[0].id) : "");
      setAcademicYears(y);
      setAcademicYear(y.length > 0 ? y[0].name : "");
    });
  }, []);

  useEffect(() => {
    if (!examId || !academicYear) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    getExamGradingPassFailSummary(examId, academicYear)
      .then(setRows)
      .catch(() => setError("Failed to load summary."))
      .finally(() => setLoading(false));
  }, [examId, academicYear]);

  const gradingColumns = [
    { key: "classConfigName", label: "Class" },
    { key: "totalStudents", label: "Total Students" },
    { key: "numOfAPlus", label: "A+" },
    { key: "numOfA", label: "A" },
    { key: "numOfAMinus", label: "A-" },
    { key: "numOfB", label: "B" },
    { key: "numOfC", label: "C" },
    { key: "numOfD", label: "D" },
    { key: "numOfF", label: "F" },
  ];

  const passFailColumns = [
    { key: "classConfigName", label: "Class" },
    { key: "totalStudents", label: "Total Students" },
    { key: "participatedInExam", label: "Participated" },
    { key: "totalPass", label: "Pass" },
    { key: "totalFail", label: "Fail" },
    { key: "passPercentage", label: "Pass %", render: (row) => `${row.passPercentage}%` },
    { key: "failPercentage", label: "Fail %", render: (row) => `${row.failPercentage}%` },
  ];

  const rowsWithData = rows.filter((r) => r.totalStudents > 0 || r.participatedInExam > 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>{tab === "grading" ? "Section Wise Grading Summary" : "Section Wise Pass & Fail Summary"}</h1>
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          Exam
          <select value={examId} onChange={(e) => setExamId(e.target.value)}>
            {exams.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable columns={tab === "grading" ? gradingColumns : passFailColumns} rows={rowsWithData} />
      )}
      {!loading && rowsWithData.length === 0 && <p>No students found for this exam and academic year.</p>}
    </MainLayout>
  );
}
